import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { DatePicker } from "@material-ui/pickers";

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';

import CloseIcon from '@material-ui/icons/Close';

import SearchUsersAutoComplete from 'app/components/user/UserSearchAutocomplete';
import Loading from 'app/components/loaders/Loading';
import RedButton from 'app/components/buttons/RedButton';

import useApi from 'app/context/useApi';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        margin="normal"
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    notActive: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        textAlign: 'center'
    }
}));

const initValues = {
    id: 0,
    amount: 0.00,
    description: '',
    paid_at: DateTime.local(),
    charge_id: null
};

export default function EditPaymentDialog(props) {

    const { payment, invoice, open, onClose, onClosed, onSave } = props;

    const [ values, setValues ] = useState(initValues);
    const [ user, setUser ] = useState({id: 0})

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();

    useEffect(() => {

        if (payment) {
            const newValues = {...initValues};

            Object.keys(initValues).forEach((i) => {
                if (payment[i]) {
                    if (i == 'paid_at') {
                        newValues[i] = DateTime.fromISO(payment[i]);
                    } else {
                        newValues[i] = payment[i];
                    }
                }
            });

            setValues(newValues);

            if (payment.user) setUser(payment.user);
        }

        setErrors({});
    }, [payment]);

    useEffect(() => {

        if (invoice.user) setUser(invoice.user);
        if (invoice.amount_due) setValues({...values, amount: invoice.amount_due})

        setErrors({});
    }, [invoice]);

    // Setter Functions ------------------------------------------

    const handleChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    const handlePaidAt = value => {
        setValues({...values, paid_at: value })
    }

    const handleUser = user => {
        if (isError('user_id')) delete errors.user_id;

        setUser(user);
    }

    // Post functions ------------------------------------------

    const postPayment = () => {

        setLoading(true);

        const data = {
            ...values,
            user_id: user.id,
            invoice_id: invoice.id
        };

        if (values.paid_at) {
            data.paid_at = values.paid_at.toString()
        }

        api.post('/admin/accounting/payment/' + data.id, data)
            .then(() => onSave())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    const deletePayment = () => {

        setLoading(true);

        api.delete('/admin/accounting/payment/' + values.id)
            .then(() => onSave())
            .finally( () => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const handleClosed = () => {
        setValues(initValues);
        setUser({id: 0})
        setLoading(false);

        if (onClosed) onClosed();
    }

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    return (
        <Dialog
            aria-labelledby="edit-payment-dialog-title"
            open={open}
            aria-describedby="edit-payment-dialogue-description"
            fullWidth
            maxWidth="sm"
            onExited={handleClosed}
            onClose={onClose}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-payment-dialog-title">
                <span>{(values.id > 0) ? "Edit Payment" : "New Payment"}</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    Invoice Paid By:
                </Typography>
                {(user.id > 0) ? (
                    <CardHeader
                        avatar={
                            <Avatar
                                aria-label={user.full_name}
                                alt={user.full_name}
                                src={user.avatar ? user.avatar.full_url : null}
                                >
                                {initials(user)}
                            </Avatar>
                        }
                        title={user.full_name}
                        subheader={user.membership_title}
                        action={
                            <IconButton
                                aria-label="Remove User"
                                onClick={() => handleUser({id:0})}
                                disabled={values.charge_id ? true : false}
                                >
                                <CloseIcon />
                            </IconButton>
                        }
                    />
                ) : (
                    <SearchUsersAutoComplete
                        closeOnSelect
                        setOnSelect
                        onSelect={handleUser}
                        label="Find User to Pay Invoice"
                        admin
                    />
                )}
                <TextField
                    id="payment-description"
                    label="Description"
                    placeholder="(i.e. check #)"
                    multiline
                    rows="4"
                    rowsMax="4"
                    variant="outlined"
                    fullWidth
                    value={values.description}
                    error={isError('description')}
                    onChange={handleChange('description')}
                    helperText={getErrorMessage('description')}
                    margin="normal"
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Amount"
                    variant="outlined"
                    value={values.amount}
                    onChange={handleChange('amount')}
                    error={isError('amount')}
                    helperText={getErrorMessage('amount')}
                    type="number"
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: {
                            min: 0,
                            step: 0.01
                        }
                    }}
                    disabled={values.charge_id ? true : false}
                />
                <DatePicker
                    label="Date Paid"
                    inputVariant="outlined"
                    value={values.paid_at}
                    onChange={handlePaidAt}
                    minutesStep={5}
                    fullWidth
                    margin="normal"
                    format="MM/dd/yyyy"
                    disabled={values.charge_id ? true : false}
                />
            </DialogContent>
            <DialogActions>
                { values.id > 0 &&
                    <RedButton onClick={deletePayment} variant="outlined">
                        Delete
                    </RedButton>
                }
                <Button
                    onClick={postPayment}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
